import { fill } from '../strings';
/**
 * Calendar Team View (piece 12): a manager sees a direct report's calendar
 * two ways. Overlay layers the report's events onto the manager's own grid in
 * that report's colour; View Only shows exactly one report's calendar and
 * nothing else. My Calendar is always shown and cannot be unchecked, and the
 * banner subtitle always states the mode and whose calendars are on screen.
 */
export interface TeamMember {
  personId: string;
  name: string;
  /** The palette token the person's avatar carries, which is also their overlay colour. */
  avatarColor: string;
}

export interface TeamSelection {
  /** The reports overlaid on the manager's own grid, by person. */
  overlay: readonly string[];
  /** One report whose calendar replaces the grid, or none. */
  viewOnly: string | null;
}

export const MY_CALENDAR: TeamSelection = { overlay: [], viewOnly: null };

/** Whose events the read asks for: the one person in View Only, else the manager and every overlaid report. */
export function ownersFor(viewerPersonId: string, selection: TeamSelection): string[] {
  if (selection.viewOnly) return [selection.viewOnly];
  return [viewerPersonId, ...selection.overlay.filter((id) => id !== viewerPersonId)];
}

/** 🔨 My Calendar is never unchecked; toggling a report adds or removes it from the overlay and leaves View Only. */
export function toggleOverlay(selection: TeamSelection, personId: string): TeamSelection {
  const overlay = selection.overlay.includes(personId) ? selection.overlay.filter((id) => id !== personId) : [...selection.overlay, personId];
  return { overlay, viewOnly: null };
}

export function viewOnly(selection: TeamSelection, personId: string | null): TeamSelection {
  return { overlay: selection.overlay, viewOnly: personId };
}

/** "R. Springer" from "Rosa Springer": the short form the chips and the subtitle use. */
export function shortName(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length < 2) return name.trim();
  return `${parts[0]!.charAt(0)}. ${parts[parts.length - 1]}`;
}

export interface TeamWording {
  overlay: string;
  myCalendar: string;
  viewing: string;
  calendarOf: string;
}

/** The banner subtitle: "Overlay · My Calendar + R. Springer + A. Delgado", or "Viewing · R. Springer's Calendar"; null when nobody else is shown. */
export function teamSubtitle(selection: TeamSelection, members: readonly TeamMember[], words: TeamWording): string | null {
  const nameOf = (id: string) => shortName(members.find((m) => m.personId === id)?.name ?? '');
  if (selection.viewOnly) return `${words.viewing} · ${nameOf(selection.viewOnly)}${words.calendarOf}`;
  if (selection.overlay.length === 0) return null;
  return `${words.overlay} · ${words.myCalendar}${selection.overlay.map((id) => ` + ${nameOf(id)}`).join('')}`;
}

/** The dropdown's closed label: "My Calendar", "My Calendar + 2 selected", or the one person viewed. */
export function teamControlLabel(selection: TeamSelection, members: readonly TeamMember[], words: TeamWording & { selected: string }): string {
  if (selection.viewOnly) return `${words.viewing}: ${shortName(members.find((m) => m.personId === selection.viewOnly)?.name ?? '')}`;
  if (selection.overlay.length === 0) return words.myCalendar;
  return `${words.myCalendar} + ${fill(words.selected, { n: String(selection.overlay.length) })}`;
}
